import { useAuth } from "@/hooks/useAuth";
import { useUserPreference } from "@/hooks/useUserPreference";
import { useLocation } from "wouter";
import { Calendar, ChevronDown, ChevronRight, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import UserAvatar from "@/components/common/UserAvatar";
import { useQuery } from "@tanstack/react-query";
import { Project, ProjectAssignment, HolidayCalendar } from "@shared/schema";
import { differenceInDays, format, formatDistanceToNow } from "date-fns";
import { useState, useEffect, useRef, useCallback } from "react";
import { menuGroups, canAccessItem, type MenuItem } from "@/config/navigation";
import { useSidebarCounts } from "@/hooks/useSidebarCounts";

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const SIDEBAR_STORAGE_KEY = 'sidebar-expanded-groups-v2';

export default function Sidebar({ isOpen, setIsOpen }: SidebarProps) {
  const { user, isAdmin, isSuperAdmin, isTeamHead, isTeamLead, canManageLeaveHr, canViewOrgAnalytics, canManageContent } = useAuth();
  const [location, navigate] = useLocation();
  const sidebarRef = useRef<HTMLElement>(null);
  const { counts } = useSidebarCounts();

  // Fetch holiday calendars to show user's assigned calendar
  const { data: holidayCalendars } = useQuery<HolidayCalendar[]>({
    queryKey: ["/api/holiday-calendars"],
    enabled: !!user?.holidayCalendarId,
  });
  const userCalendar = holidayCalendars?.find(c => c.id === user?.holidayCalendarId);

  // Map menu item paths to their badge counts
  const badgeCounts: Record<string, number | undefined> = {
    "/leave": counts?.myPendingLeaves,
    "/my-tasks": counts?.overdueTasks,
    "/leave-approvals": counts?.leaveApprovals,
    "/people-teams": counts?.joinRequests,
    "/request-access": counts?.accessRequests,
  };

  // Scroll the active nav item into view when location changes
  const scrollActiveIntoView = useCallback(() => {
    if (!sidebarRef.current) return;
    // Small delay to ensure the DOM has updated after expand/navigation
    requestAnimationFrame(() => {
      const activeItem = sidebarRef.current?.querySelector('[data-active="true"]');
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }, []);

  useEffect(() => {
    scrollActiveIntoView();
  }, [location, scrollActiveIntoView]);

  // Initialize expanded state from localStorage or defaults
  const getInitialExpandedState = (): Record<string, boolean> => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading sidebar state:', error);
    }
    // Default state if nothing in localStorage — non-admins only see Work expanded
    const isManagement = isAdmin || isTeamHead || isTeamLead;
    return {
      main: true,
      work: true,
      management: isManagement,
      analytics: false,
      'team--learning': false,
      admin: false,
    };
  };
  
  // Track expanded state for each group
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(getInitialExpandedState);
  
  // Save to localStorage whenever expanded state changes
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(expandedGroups));
    } catch (error) {
      console.error('Error saving sidebar state:', error);
    }
  }, [expandedGroups]);

  // Auto-expand group containing active page
  useEffect(() => {
    // Find which group contains the active page
    for (const group of menuGroups) {
      const hasActivePage = group.items.some(item => {
        if (item.path === "/") {
          return location === "/";
        }
        return location === item.path || location.startsWith(item.path + "/");
      });
      
      if (hasActivePage) {
        const groupKey = group.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
        if (!expandedGroups[groupKey]) {
          setExpandedGroups(prev => ({ ...prev, [groupKey]: true }));
        }
        break; // Found the active group, no need to continue
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  };

  // Check if a menu item is active
  const isItemActive = (itemPath: string) => {
    // For root path, only match exactly
    if (itemPath === "/") {
      return location === "/";
    }
    // Exact match always wins
    if (location === itemPath) return true;
    // Sub-route match, but only if no other menu item has a more specific match
    // e.g. /admin should not be active when /admin/leave is the current page
    if (location.startsWith(itemPath + "/")) {
      const allPaths = menuGroups.flatMap(g => g.items.map(i => i.path));
      const hasMoreSpecificMatch = allPaths.some(otherPath =>
        otherPath !== itemPath &&
        otherPath.length > itemPath.length &&
        (location === otherPath || location.startsWith(otherPath + "/"))
      );
      return !hasMoreSpecificMatch;
    }
    return false;
  };

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // Check if item should be visible based on access level
  const accessCtx = { isAdmin, isSuperAdmin, isTeamHead, isTeamLead, canManageLeaveHr, canViewOrgAnalytics, canManageContent };
  const checkAccess = (item: MenuItem): boolean => canAccessItem(item, accessCtx);

  // Favorites
  const [favoritePages, setFavoritePages] = useUserPreference<string[]>("sidebar.favorite_pages", []);
  const isFavorite = (path: string) => favoritePages.includes(path);
  const toggleFavorite = (path: string) => {
    setFavoritePages(
      isFavorite(path)
        ? favoritePages.filter((p) => p !== path)
        : [...favoritePages, path].slice(0, 8) // max 8 favorites
    );
  };

  // Resolve favorite paths to menu items (only accessible ones)
  const allMenuItems = menuGroups.flatMap((g) => g.items);
  const favoriteItems = favoritePages
    .map((path) => allMenuItems.find((item) => item.path === path))
    .filter((item): item is MenuItem => !!item && checkAccess(item));

  return (
    <aside
      ref={sidebarRef}
      className={`bg-background border-r border-border w-72 fixed top-0 left-0 bottom-0 pt-14 lg:pt-0 transform ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      } lg:translate-x-0 transition-transform duration-300 ease-in-out z-20 overflow-y-auto scrollbar-hide`}
    >
      <div className="p-4 pt-6">
        <div className="flex items-center gap-2 mb-6">
          <UserAvatar 
            name={user?.name || "User"} 
            src={user?.photoUrl ?? undefined} 
            size="lg" 
          />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium truncate">{user?.name}</h3>
            <p className="text-xs text-muted-foreground truncate">{user?.title}</p>
            {isAdmin ? (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 dark:text-purple-300 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-800">
                Admin
              </Badge>
            ) : isTeamHead ? (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:text-blue-300 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800">
                Team Head
              </Badge>
            ) : isTeamLead ? (
              <Badge variant="outline" className="text-xs bg-teal-50 text-teal-700 dark:text-teal-300 border-teal-200 dark:bg-teal-950/30 dark:text-teal-400 dark:border-teal-800">
                Team Lead
              </Badge>
            ) : (user?.canManageLeaveHr || user?.canViewOrgAnalytics || user?.canManageContent) ? (
              <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-800">
                Specialist
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground">Member</span>
            )}
            {userCalendar && (
              <p className="text-[11px] text-muted-foreground truncate mt-0.5" title={userCalendar.name}>
                <Calendar className="inline h-3 w-3 mr-1 -mt-px" />
                {userCalendar.name}
              </p>
            )}
          </div>
        </div>

        <nav className="space-y-3" data-tour="sidebar-nav">
          {/* Favorites Section */}
          {favoriteItems.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                <span>Favorites</span>
              </div>
              <div className="space-y-1">
                {favoriteItems.map((item) => {
                  const active = isItemActive(item.path);
                  const badgeCount = badgeCounts[item.path];
                  return (
                    <button
                      key={`fav-${item.path}`}
                      onClick={() => handleNavigation(item.path)}
                      data-active={active || undefined}
                      className={`group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md w-full transition-colors ${
                        active
                          ? "bg-primary/10 text-primary font-semibold"
                          : "text-foreground hover:bg-accent"
                      }`}
                    >
                      {item.icon}
                      <span className="truncate flex-1 text-left">{item.label}</span>
                      {badgeCount != null && badgeCount > 0 && (
                        <Badge
                          variant="destructive"
                          className="h-5 min-w-[20px] px-1.5 text-[10px] font-bold flex items-center justify-center"
                        >
                          {badgeCount > 99 ? "99+" : badgeCount}
                        </Badge>
                      )}
                      <Star
                        className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(item.path); }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {menuGroups.map((group) => {
            const groupKey = group.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
            const isExpanded = expandedGroups[groupKey];
            
            // Filter items based on access level
            const visibleItems = group.items.filter(checkAccess);
            
            // Don't render the group if no items are visible
            if (visibleItems.length === 0) return null;
            
            return (
              <div key={groupKey} className="space-y-1">
                <button
                  onClick={() => toggleGroup(groupKey)}
                  className="flex items-center justify-between w-full px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  <span>{group.title}</span>
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="space-y-1">
                    {visibleItems.map((item) => {
                      const active = isItemActive(item.path);
                      const badgeCount = badgeCounts[item.path];
                      const fav = isFavorite(item.path);
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          data-active={active || undefined}
                          data-tour={
                            item.path === "/timesheet" ? "sidebar-timesheet" :
                            item.path === "/leave" ? "sidebar-leave" :
                            undefined
                          }
                          className={`group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md w-full transition-colors ${
                            active
                              ? "bg-primary/10 text-primary font-semibold"
                              : "text-foreground hover:bg-accent"
                          }`}
                        >
                          {item.icon}
                          <span className="truncate flex-1 text-left">{item.label}</span>
                          {badgeCount != null && badgeCount > 0 && (
                            <Badge
                              variant="destructive"
                              className="h-5 min-w-[20px] px-1.5 text-[10px] font-bold flex items-center justify-center"
                            >
                              {badgeCount > 99 ? "99+" : badgeCount}
                            </Badge>
                          )}
                          <Star
                            className={`h-3.5 w-3.5 shrink-0 transition-opacity cursor-pointer ${
                              fav
                                ? "fill-yellow-400 text-yellow-400 opacity-60 group-hover:opacity-100"
                                : "text-muted-foreground opacity-0 group-hover:opacity-50 hover:!opacity-100"
                            }`}
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(item.path); }}
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      <div className="mt-6 px-4 py-3 border-t border-border">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">My Projects</h4>
          <button 
            className="text-xs text-primary hover:text-primary/80"
            onClick={() => handleNavigation("/projects")}
            aria-label="View all projects"
          >
            View All
          </button>
        </div>
        <ProjectsList userId={user?.id} />
      </div>
    </aside>
  );
}

interface ProjectsListProps {
  userId?: number;
}

function ProjectsList({ userId }: ProjectsListProps) {
  const [, navigate] = useLocation();

  // Fetch user's project assignments
  const { data: assignments, isLoading: isLoadingAssignments } = useQuery<ProjectAssignment[]>({
    queryKey: userId ? [`/api/project-assignments/user/${userId}`] : [],
    enabled: !!userId
  });

  // Fetch all projects
  const { data: projects } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
  });

  // Get projects assigned to the user or show a subset of all projects if no assignments
  // This ensures we display projects even if the user has no assignments yet
  let userProjects = projects;
  
  // If we have assignments, filter projects based on those assignments
  if (assignments?.length) {
    userProjects = projects?.filter(project => 
      assignments.some(assignment => assignment.projectId === project.id)
    );
  }
  // If we still don't have projects (or none matched), just show all available projects
  // Limited to 3 most recent/active ones if there are many
  else if (projects?.length) {
    // First get any active projects
    const activeProjects = projects.filter(p => p.status === 'active');
    
    if (activeProjects.length >= 3) {
      // If we have 3 or more active projects, just show those
      userProjects = activeProjects.slice(0, 3);
    } else {
      // Otherwise, fill with other projects up to 3 total
      const otherProjects = projects
        .filter(p => p.status !== 'active')
        .slice(0, 3 - activeProjects.length);
      
      userProjects = [...activeProjects, ...otherProjects];
    }
  }

  // Sort by status and date (active first, then by closest end date)
  const sortedProjects = userProjects?.sort((a, b) => {
    // Active projects first
    if (a.status === 'active' && b.status !== 'active') return -1;
    if (a.status !== 'active' && b.status === 'active') return 1;
    
    // Sort by end date (closest first)
    const aDate = a.endDate ? new Date(a.endDate) : new Date(9999, 11, 31);
    const bDate = b.endDate ? new Date(b.endDate) : new Date(9999, 11, 31);
    return aDate.getTime() - bDate.getTime();
  });

  const handleNavigateToProject = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  if (isLoadingAssignments) {
    return (
      <div className="mt-3 space-y-2 animate-pulse">
        <div className="h-14 bg-muted rounded-md"></div>
        <div className="h-14 bg-muted rounded-md"></div>
      </div>
    );
  }

  if (!sortedProjects || sortedProjects.length === 0) {
    return (
      <div className="mt-3 text-center py-2">
        <p className="text-xs text-muted-foreground">No assigned projects</p>
      </div>
    );
  }

  // Function to get due date display text
  const getDueDateText = (endDate?: Date | string | null) => {
    if (!endDate) return "No deadline";
    
    const date = typeof endDate === 'string' ? new Date(endDate) : endDate;
    const days = differenceInDays(date, new Date());
    
    if (days < 0) return "Past due";
    if (days === 0) return "Due today";
    if (days === 1) return "Due tomorrow";
    if (days < 7) return `Due in ${days} days`;
    if (days < 30) return `Due in ${Math.floor(days / 7)} weeks`;
    return `Due on ${format(date, 'MMM d')}`;
  };

  // Function to get badge color based on status
  const getStatusBadgeProps = (status: string) => {
    switch (status) {
      case 'active':
        return { bg: "bg-green-50 dark:bg-green-950/30", text: "text-green-700 dark:text-green-300 dark:text-green-400", border: "border-green-200 dark:border-green-800" };
      case 'review':
        return { bg: "bg-amber-50 dark:bg-amber-950/30", text: "text-amber-700 dark:text-amber-300 dark:text-amber-400", border: "border-amber-200 dark:border-amber-800" };
      case 'completed':
        return { bg: "bg-blue-50 dark:bg-blue-950/30", text: "text-blue-700 dark:text-blue-300 dark:text-blue-400", border: "border-blue-200 dark:border-blue-800" };
      default:
        return { bg: "bg-muted", text: "text-muted-foreground", border: "border-border" };
    }
  };

  return (
    <div className="mt-3 space-y-2">
      {sortedProjects.slice(0, 3).map(project => {
        const badgeProps = getStatusBadgeProps(project.status);
        return (
          <div 
            key={project.id}
            onClick={() => handleNavigateToProject(project.id)}
            className="block px-3 py-2 text-sm rounded-md hover:bg-accent cursor-pointer transition-colors"
          >
            <div className="flex justify-between items-start gap-2">
              <span className="font-medium truncate flex-1">{project.name}</span>
              <Badge 
                variant="outline" 
                className={`${badgeProps.bg} ${badgeProps.text} ${badgeProps.border} text-xs shrink-0`}
              >
                {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
              </Badge>
            </div>
            <div className="mt-1 text-xs text-muted-foreground truncate">
              {getDueDateText(project.endDate)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
